// API configuration for Cloudflare Workers backend
const API_URL = 'https://safetyculture-api.balashovn.workers.dev/api';

// Base fetch wrapper
async function fetchApi(endpoint: string, options: RequestInit = {}) {
  const url = `${API_URL}${endpoint}`;
  const token = localStorage.getItem('token');
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    ...options,
    headers
  });
  
  if (response.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }
  
  // Handle empty responses
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    return response.json();
  }
  return response.text();
}

// Auth API
export const authApi = {
  login: (email: string, password: string) => 
    fetchApi('/auth/login', { 
      method: 'POST', 
      body: JSON.stringify({ email, password }) 
    }),
  register: (data: any) => 
    fetchApi('/auth/register', { 
      method: 'POST', 
      body: JSON.stringify(data) 
    }),
  me: () => fetchApi('/auth/me')
};

// Templates API
export const templatesApi = {
  getAll: (params?: any) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return fetchApi(`/templates${query}`);
  },
  getById: (id: string) => fetchApi(`/templates/${id}`),
  create: (data: any) => 
    fetchApi('/templates', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => 
    fetchApi(`/templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => 
    fetchApi(`/templates/${id}`, { method: 'DELETE' }),
  clone: (id: string) => 
    fetchApi(`/templates/${id}/clone`, { method: 'POST' }),
  getCategories: () => fetchApi('/templates/meta/categories')
};

// Inspections API
export const inspectionsApi = {
  getAll: (params?: any) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return fetchApi(`/inspections${query}`);
  },
  getById: (id: string) => fetchApi(`/inspections/${id}`),
  create: (data: any) => 
    fetchApi('/inspections', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => 
    fetchApi(`/inspections/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => 
    fetchApi(`/inspections/${id}`, { method: 'DELETE' }),
  sync: (inspections: any[]) => 
    fetchApi('/inspections/sync', { method: 'POST', body: JSON.stringify({ inspections }) })
};

// Tasks API
export const tasksApi = {
  getAll: (params?: any) => {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return fetchApi(`/tasks${query}`);
  },
  getById: (id: string) => fetchApi(`/tasks/${id}`),
  create: (data: any) => 
    fetchApi('/tasks', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => 
    fetchApi(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => 
    fetchApi(`/tasks/${id}`, { method: 'DELETE' })
};

// Reports API
export const reportsApi = {
  generate: (inspectionId: string, type: string = 'json') => 
    fetchApi(`/reports/generate/${inspectionId}`, { 
      method: 'POST', 
      body: JSON.stringify({ type }) 
    })
};

// Analytics API
export const analyticsApi = {
  getOverview: () => fetchApi('/analytics/overview'),
  getTrends: (days?: number) => {
    const query = days ? `?days=${days}` : '';
    return fetchApi(`/analytics/trends${query}`);
  },
  getTasks: () => fetchApi('/analytics/tasks')
};

// Media API
export const mediaApi = {
  upload: async (file: File, data: any) => {
    // Convert file to base64
    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
    
    return fetchApi('/media/upload', {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        data: base64,
        filename: file.name
      })
    });
  },
  getUrl: (id: string) => `${API_URL}/media/${id}`,
  delete: (id: string) => 
    fetchApi(`/media/${id}`, { method: 'DELETE' })
};

export default fetchApi;